import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/use-auth-wrapper'
import { usePermissions } from '@/hooks/use-permissions'
import { useThemeStore } from '@/stores/theme.store'
import { useChurch } from '@/hooks/useChurch'
import { useChurchColors } from '@/hooks/useChurchColors'
import { logger } from '@/lib/logger'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { getInitials } from '@/lib/utils'
import {
  LayoutDashboard,
  Calendar,
  Book,
  Users,
  UserCircle,
  User,
  LogOut,
  Moon,
  Sun,
  ChevronRight,
  Sparkles,
  ScrollText,
  DollarSign,
  Shield,
  Building,
  HandHeart,
} from 'lucide-react'

// Definindo tipos para a estrutura de navegação
interface NavigationChild {
  name: string
  href: string
  children?: NavigationChild[]
}

interface NavigationItem {
  name: string
  href?: string
  icon: React.ComponentType<{ className?: string }>
  badge?: string
  children?: NavigationChild[]
}

// const mockMinisterios = [
//   { id: '1', nome: 'Ministério de Louvor' },
//   { id: '2', nome: 'Ministério de Jovens' },
//   { id: '3', nome: 'Ministério de Crianças' },
//   { id: '4', nome: 'Ministério de Ação Social' },
//   { id: '5', nome: 'Ministério de Intercessão' },
//   { id: '6', nome: 'Ministério de Recepção' },
// ]

const navigation: NavigationItem[] = [
  { name: 'Principal', href: '/', icon: LayoutDashboard },
  { name: 'Contribuição', href: '/contribuicao', icon: HandHeart },
  { name: 'Eventos', href: '/eventos', icon: Calendar, badge: '3' },
  { name: 'Bíblia', href: '/biblia', icon: Book },
  { name: 'Ministérios', href: '/ministerios', icon: Users },
  { name: 'Membros', href: '/membros', icon: UserCircle },
  { name: 'Sermao/Palavra', href: '/sermoes', icon: ScrollText },
  { name: 'Finanças', href: '/financas', icon: DollarSign },
]

export function Sidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { perfil, logout } = useAuth()
  const { canManageChurch } = usePermissions()
  const { theme, setTheme } = useThemeStore()
  const { church, currentLogo, hasLogo } = useChurch()
  const { primaryColor, secondaryColor, titleColor, descriptionColor } = useChurchColors()
  const avatarUrl = (perfil as { avatar_url?: string } | null)?.avatar_url

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light')
  }

  // Criar array de navegação dinâmico baseado nas permissões do usuário
  const dynamicNavigation = React.useMemo(() => {
    const nav = [...navigation]
    
    // Adicionar "Minha Igreja" se o usuário for admin ou gestor
    if (canManageChurch()) {
      nav.push({ name: 'Minha Igreja', href: '/minha-igreja', icon: Building })
    }
    
    // Adicionar Admin App se o usuário for admin
    if (perfil?.is_admin) {
      nav.push({ name: 'Admin App', href: '/admin-app', icon: Shield })
    }
    
    return nav
  }, [perfil?.is_admin, canManageChurch])

  const handleLogout = async () => {
    logger.info('Iniciando logout', { context: 'SIDEBAR' })
    try {
      await logout()
      logger.info('Logout concluído', { context: 'SIDEBAR' })
    } catch {
      logger.error('Erro no logout', { context: 'SIDEBAR' })
    } finally {
      logger.info('Redirecionando para login', { context: 'SIDEBAR' })
      navigate('/login', { replace: true })
    }
  }

  return (
    <div className="flex h-full flex-col bg-card dark:bg-[hsl(217.2_32.6%_17.5%)]">
      {/* Logo */}
      <div className="flex h-20 items-center border-b px-6 py-4">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-2"
        >
          {/* Logo da Igreja ou Ícone Padrão */}
          {/* Logo da Igreja */}
          {hasLogo && currentLogo ? (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
            >
              <img 
                src={currentLogo} 
                alt={`Logo ${church?.nome || 'Igreja'}`}
                className="h-14 w-14 object-contain"
                onError={(e) => {
                  // Esconde a imagem se não carregar
                  e.currentTarget.style.display = 'none'
                }}
              />
            </motion.div>
          ) : (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
              className="flex h-14 w-14 items-center justify-center rounded-lg"
              style={{
                backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}b3)`
              }}
            >
              <Sparkles className="h-8 w-8 text-white" />
            </motion.div>
          )}
          <div className="flex flex-col min-w-0 flex-1 text-center">
            <h1 
              key={`title-${titleColor}`}
              className="text-xl font-bold truncate"
              style={{
                color: titleColor
              }}
              title={church?.nome || 'IgrejaDigital'}
            >
              {church?.nome || 'IgrejaDigital'}
            </h1>
            {church?.descricao && (
              <p 
                key={`description-${descriptionColor}`}
                className="text-xs mt-1 opacity-75 truncate"
                style={{
                  color: descriptionColor
                }}
                title={church.descricao}
              >
                {church.descricao}
              </p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-3 py-6">
        {dynamicNavigation.map((item, index) => {
          const isActive = item.href
            ? location.pathname === item.href
            : item.children?.some(
                (child: NavigationChild) =>
                  child.href === location.pathname ||
                  child.children?.some((subChild: NavigationChild) => subChild.href === location.pathname)
              )

          return (
            <div key={item.name} className="group relative">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                {item.href ? (
                  <Link
                    to={item.href}
                    className={cn(
                      'group relative flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-all',
                      isActive
                        ? 'text-white shadow-md'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                    style={isActive ? {
                      backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}b3)`,
                      boxShadow: `0 4px 12px ${primaryColor}20`
                    } : {}}
                  >
                    <item.icon
                      className={cn('h-5 w-5 transition-transform', isActive && 'scale-110')}
                    />
                    <span className="flex-1">{item.name}</span>
                    {item.badge && (
                      <Badge
                        variant={isActive ? 'secondary' : 'outline'}
                        className="h-5 min-w-5 px-1 text-xs"
                        style={isActive ? {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          borderColor: 'rgba(255, 255, 255, 0.3)'
                        } : {
                          backgroundColor: `${secondaryColor}20`,
                          color: secondaryColor,
                          borderColor: `${secondaryColor}40`
                        }}
                      >
                        {item.badge}
                      </Badge>
                    )}
                    {isActive && (
                      <motion.div
                        layoutId="activeIndicator"
                        className="absolute right-2"
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </motion.div>
                    )}
                  </Link>
                ) : (
                  <div
                    className={cn(
                      'group relative flex cursor-pointer items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-all',
                      isActive
                        ? 'text-white shadow-md'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                    style={isActive ? {
                      backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}b3)`,
                      boxShadow: `0 4px 12px ${primaryColor}20`
                    } : {}}
                  >
                    <item.icon
                      className={cn('h-5 w-5 transition-transform', isActive && 'scale-110')}
                    />
                    <span className="flex-1">{item.name}</span>
                    <ChevronRight
                      className={cn('h-4 w-4 transition-transform', 'group-hover:rotate-90')}
                    />
                  </div>
                )}
              </motion.div>

              {/* Submenu Level 1 */}
              {item.children && (
                <div className="invisible absolute left-full top-0 ml-2 min-w-[200px] rounded-xl border bg-card shadow-lg group-hover:visible">
                  {item.children.map((child) => (
                    <div key={child.name} className="group/child relative">
                      {child.href ? (
                        <Link
                          to={child.href}
                          className={cn(
                            "block px-4 py-2 text-sm transition-colors",
                            location.pathname === child.href
                              ? "text-white"
                              : "text-foreground/70 hover:bg-accent hover:text-accent-foreground"
                          )}
                          style={location.pathname === child.href ? {
                            backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}b3)`
                          } : {}}
                        >
                          {child.name}
                        </Link>
                      ) : (
                        <div className="relative flex items-center justify-between px-4 py-2 text-sm text-foreground/70 hover:bg-accent hover:text-accent-foreground">
                          {child.name}
                          <ChevronRight className="h-4 w-4" />
                        </div>
                      )}

                      {/* Submenu Level 2 */}
                      {child.children && (
                        <div className="invisible absolute left-full top-0 min-w-[200px] rounded-xl border bg-card shadow-lg group-hover/child:visible">
                          {child.children.map((subChild) => (
                            <Link
                              key={subChild.name}
                              to={subChild.href}
                              className={cn(
                                "block px-4 py-2 text-sm transition-colors",
                                location.pathname === subChild.href
                                  ? "text-white"
                                  : "text-foreground/70 hover:bg-accent hover:text-accent-foreground"
                              )}
                              style={location.pathname === subChild.href ? {
                                backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}b3)`
                              } : {}}
                            >
                              {subChild.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {/* User Profile */}
      <div className="border-t bg-card dark:bg-[hsl(217.2_32.6%_17.5%)] p-2">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="rounded-lg border bg-card dark:bg-[hsl(217.2_32.6%_17.5%)] p-2 shadow-sm"
        >
          <div className="mb-2 flex items-center gap-2">
            <div className="relative">
              <Avatar 
                className="h-8 w-8 border"
                style={{ borderColor: `${primaryColor}33` }}
              >
                <AvatarImage src={avatarUrl} />
                <AvatarFallback 
                  className="text-xs text-white"
                  style={{
                    backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}b3)`
                  }}
                >
                  {getInitials(perfil?.nome || 'U')}
                </AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border border-background bg-emerald-500" />
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="truncate text-xs font-medium">{perfil?.nome}</p>
              <Badge 
                variant="secondary" 
                className="h-4 text-[10px] capitalize"
                style={{
                  backgroundColor: `${secondaryColor}20`,
                  color: secondaryColor,
                  borderColor: `${secondaryColor}40`
                }}
              >
                {perfil?.permissao}
              </Badge>
              {perfil?.igreja?.nome && (
                <p className="truncate text-[10px] text-muted-foreground mt-0.5">
                  {perfil.igreja.nome}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/perfil')}
              className="h-7 flex-1 px-2"
              style={{
                borderColor: `${primaryColor}40`,
                '--ring': primaryColor
              } as React.CSSProperties}
              title="Perfil"
            >
              <User className="h-3 w-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleTheme}
              className="h-7 flex-1 px-2"
              style={{
                borderColor: `${primaryColor}40`,
                '--ring': primaryColor
              } as React.CSSProperties}
              title="Alternar tema"
            >
              {theme === 'light' ? <Moon className="h-3 w-3" /> : <Sun className="h-3 w-3" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="h-7 flex-1 px-2"
              style={{
                borderColor: `${primaryColor}40`,
                '--ring': primaryColor
              } as React.CSSProperties}
              title="Sair"
            >
              <LogOut className="h-3 w-3" />
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
